import React, { useEffect, useMemo, useState } from 'react';
import EmbeddedCheckoutComponent from '../EmbeddedCheckout';
import OrderSummary from '../OrderSummary';
import { plans } from '../../data/plans';
import { getSubscriptionPlans, SubscriptionPlan } from '../../services/credits';
import Portal from '../Portal';
import { useScrollLock } from '../../hooks/useScrollLock';
import { useFocusTrap } from '../../hooks/useFocusTrap';
import { useViewportHeight } from '../../hooks/useViewportHeight';

interface StripeCheckoutModalProps {
  open: boolean;
  onClose: () => void;
  clientSecret: string | null;
  customerId: string | null;
  planId: string | null;
  billingPeriod: 'monthly' | 'annual';
  publishableKey?: string | null;
  userId?: string;
  onComplete?: () => void;
  onError?: (error: Error) => void;
}

export default function StripeCheckoutModal({
  open,
  onClose,
  clientSecret,
  customerId,
  planId,
  billingPeriod,
  publishableKey,
  userId,
  onComplete,
  onError,
}: StripeCheckoutModalProps) {
  const [stripePlans, setStripePlans] = useState<Record<string, SubscriptionPlan>>({});

  // Use custom hooks for modal behavior
  useScrollLock(open);
  useViewportHeight();
  const focusTrapRef = useFocusTrap(open);

  // Fetch Stripe plan prices on mount
  useEffect(() => {
    const fetchPlans = async () => {
      try {
        const response = await getSubscriptionPlans();
        const plansMap: Record<string, SubscriptionPlan> = {};
        response.plans.forEach((plan) => {
          plansMap[plan.id] = plan;
        });
        setStripePlans(plansMap);
      } catch (err) {
        console.error('Failed to fetch Stripe plans:', err);
        // Continue with default plans if fetch fails
      }
    };
    fetchPlans();
  }, []);

  // Handle ESC key to close
  useEffect(() => {
    if (!open) return;

    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        onClose();
      }
    };

    document.addEventListener('keydown', handleEscape);
    return () => document.removeEventListener('keydown', handleEscape);
  }, [open, onClose]);

  // Handle backdrop click
  const handleBackdropClick = (e: React.MouseEvent) => {
    if (e.target === e.currentTarget) {
      onClose();
    }
  };

  // Get plan data for order summary
  const planData = useMemo(() => {
    if (!planId) return null;
    return plans.find(p => p.id === planId);
  }, [planId]);

  // Calculate pricing from Stripe data
  const orderSummaryData = useMemo(() => {
    if (!planData || !planId) return null;

    const stripePlan = stripePlans[planId];
    if (!stripePlan) return null;

    const monthlyPrice = stripePlan.price_monthly_usd;
    const annualPrice = stripePlan.price_annual_usd;
    
    const totalAmount = billingPeriod === 'annual' ? annualPrice : monthlyPrice;
    const monthlyEquivalent = billingPeriod === 'annual' ? annualPrice / 12 : undefined;

    return {
      planName: planData.name,
      totalAmount,
      monthlyEquivalent,
    };
  }, [planData, planId, stripePlans, billingPeriod]);

  if (!open) return null;

  return (
    <Portal>
      <div
        className="fixed inset-0 bg-black/80 backdrop-blur-sm z-[9999] flex items-end lg:items-center justify-center p-2 lg:p-0"
        onClick={handleBackdropClick}
      >
        <div
          ref={focusTrapRef}
          className="w-full h-[calc(100dvh-1rem)] lg:h-auto lg:max-h-[90vh] max-w-4xl lg:mx-4 bg-bg border border-white/20 rounded-t-3xl lg:rounded-xl flex flex-col shadow-2xl"
          onClick={(e) => e.stopPropagation()}
          role="dialog"
          aria-modal="true"
          aria-labelledby="checkout-modal-title"
        >
          {/* Scrollable Body */}
          <div
            className="flex-1 overflow-y-auto overscroll-contain px-4 sm:px-6 pt-[calc(0.75rem+env(safe-area-inset-top,0.5rem))] pb-3 sm:pb-4"
            data-scroll-lock-ignore
          >
            {/* Back Button */}
            <button
              onClick={onClose}
              className="flex items-center gap-1.5 text-white/60 hover:text-white/80 transition-colors group mb-3"
              aria-label="Close checkout and return to upgrade page"
            >
              <svg
                className="w-3.5 h-3.5 transform group-hover:-translate-x-0.5 transition-transform"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2.5}
                  d="M10 19l-7-7m0 0l7-7m-7 7h18"
                />
              </svg>
              <span className="text-sm">Back</span>
            </button>

            {/* Order Summary */}
            {orderSummaryData && (
              <OrderSummary
                planName={orderSummaryData.planName}
                billingCadence={billingPeriod}
                amount={orderSummaryData.totalAmount}
                monthlyEquivalent={orderSummaryData.monthlyEquivalent}
              />
            )}

            {/* Stripe Embedded Checkout */}
            {clientSecret && customerId && planId && userId ? (
              <EmbeddedCheckoutComponent
                clientSecret={clientSecret}
                customerId={customerId}
                planId={planId}
                billingPeriod={billingPeriod}
                publishableKey={publishableKey || undefined}
                userId={userId}
                onComplete={onComplete}
                onError={onError}
              />
            ) : (
              <div className="text-white/70 text-center py-8">
                Loading checkout...
              </div>
            )}
          </div>
        </div>
      </div>
    </Portal>
  );
}

